import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createProductGroupApi,
  deleteProductGroupApi,
  getProductGroupApi,
  getProductGroupsApi,
  updateProductGroupApi,
} from "../api/productGroupsApi";
import {
  catalogQueryKey,
  type CatalogScope,
} from "../api/catalogScope";
import type { ProductGroupId } from "@KassaroAB/shared/types/ids";
import type {
  RestCreateProductGroup,
  RestUpdateProductGroup,
} from "@KassaroAB/shared/types/productGroup";

const productGroupsQueryKey = (scope: CatalogScope | null) =>
  catalogQueryKey(scope, "productGroups");

const invalidateProductGroupQueries = (
  queryClient: ReturnType<typeof useQueryClient>,
  scope: CatalogScope | null,
) => {
  queryClient.invalidateQueries({ queryKey: productGroupsQueryKey(scope) });
};

export const useGetProductGroupsQuery = (scope: CatalogScope | null) => {
  return useQuery({
    queryKey: productGroupsQueryKey(scope),
    queryFn: () => getProductGroupsApi(scope!),
    enabled: !!scope,
  });
};

export const useGetProductGroupQuery = (
  scope: CatalogScope | null,
  groupId: ProductGroupId | null | undefined,
) => {
  return useQuery({
    queryKey: [...productGroupsQueryKey(scope), groupId],
    queryFn: () => getProductGroupApi(scope!, groupId!),
    enabled: !!scope && !!groupId,
  });
};

export const useCreateProductGroupMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (group: RestCreateProductGroup) =>
      createProductGroupApi(scope!, group),
    onSuccess: () => {
      invalidateProductGroupQueries(queryClient, scope);
    },
  });
};

export const useUpdateProductGroupMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      groupId,
      group,
    }: {
      groupId: ProductGroupId;
      group: RestUpdateProductGroup;
    }) => updateProductGroupApi(scope!, groupId, group),
    onSuccess: () => {
      invalidateProductGroupQueries(queryClient, scope);
    },
  });
};

export const useDeleteProductGroupMutation = (scope: CatalogScope | null) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (groupId: ProductGroupId) =>
      deleteProductGroupApi(scope!, groupId),
    onSuccess: () => {
      invalidateProductGroupQueries(queryClient, scope);
    },
  });
};
